import { find } from 'linkifyjs'

const LINK_CLASS = 'underline'

const HTTP_URL = /^https?:\/\//i

/**
 * linkifyjs slows down quadratically on long whitespace-free text (e.g. `a.b.a.b.…`), so text longer
 * than this is shown as-is instead of risking a frozen tab.
 */
export const MAX_LINKIFY_LENGTH = 5000

/** Text inside these elements is never linkified (already a link, code, form/embedded content). */
const SKIP_TAGS = new Set(['A', 'SCRIPT', 'STYLE', 'TEXTAREA', 'IFRAME', 'BUTTON', 'SVG', 'CODE', 'PRE'])

const hasSkippedAncestor = (node: Node): boolean => {
	for (let el = node.parentElement; el; el = el.parentElement) {
		if (SKIP_TAGS.has(el.tagName.toUpperCase())) return true
	}
	return false
}

const toLinkedFragment = (text: string, doc: Document): DocumentFragment | null => {
	if (text.length > MAX_LINKIFY_LENGTH) return null

	const matches = find(text, 'url').filter(match => match.isLink)
	if (!matches.length) return null

	const fragment = doc.createDocumentFragment()
	let cursor = 0
	for (const match of matches) {
		// Only http(s): stored text is arbitrary, so never emit `javascript:`, `mailto:`, `ftp:` etc.
		if (!HTTP_URL.test(match.href)) continue
		const href = HTTP_URL.test(match.value) ? match.href : match.href.replace(/^http:/i, 'https:')

		if (match.start > cursor) fragment.append(text.slice(cursor, match.start))
		const anchor = doc.createElement('a')
		anchor.setAttribute('href', href)
		anchor.setAttribute('target', '_blank')
		anchor.setAttribute('rel', 'noopener noreferrer')
		anchor.className = LINK_CLASS
		anchor.textContent = match.value
		fragment.append(anchor)
		cursor = match.end
	}
	if (cursor === 0) return null
	if (cursor < text.length) fragment.append(text.slice(cursor))
	return fragment
}

/**
 * Wraps bare URLs found in the *text nodes* of an HTML string in `<a>` tags. Attribute values
 * (`src`, `href`, …) and existing links are left untouched, so it is safe on structured HTML —
 * unlike the string-regex `processRichTextWithLinks`. Anchors are built with DOM APIs, never by
 * concatenating HTML. Parses into a `<template>` so leading `<style>`/`<link>` elements survive.
 */
export const linkifyHtmlTextNodes = (html: string): string => {
	if (!html || typeof document === 'undefined') return html

	const template = document.createElement('template')
	template.innerHTML = html
	const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT)
	const textNodes: Text[] = []
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		if (!hasSkippedAncestor(node)) textNodes.push(node as Text)
	}

	let changed = false
	for (const node of textNodes) {
		const fragment = toLinkedFragment(node.data, document)
		if (!fragment) continue
		node.replaceWith(fragment)
		changed = true
	}
	return changed ? template.innerHTML : html
}
